import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AppOAuth2CredentialsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOAuth2CredentialsResponse';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {OAuth2ProviderName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/OAuth2';
import {EncodingUtil} from '@mintdata-wsp/common/util/EncodingUtil';
import {LocalStorageUtil} from '@mintdata-wsp/common/util/LocalStorageUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import {enc, HmacSHA1} from 'crypto-js';

export class AuthFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'auth';
    }

    @Function({
        title: 'OAUTH_SIGN(oauthConsumerKey, oauthConsumerSecret, oauthToken, oauthTokenSecret, httpMethod, url, queryParams [, body])',
        tip: 'Generates Authorization header for OAuth 1.0 flow.',
        alias: 'OAUTH_SIGN',
        example: 'OAUTH_SIGN("xvz1evFS4wEEPTGEFPHBog, HMG872JHAGHAbasdhkfgMBsdbbrtaSfQgmcL13dssFDAvhwjKAnWV, 8623818223, 8623818223-fQgmcL13dssFDAasfaf712hhJJKHKAGSflasjd01kHvh")',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Frame with Authorization header',
                tip: 'Returns Authorization header with all necessary data.'
            }
    })
    @bind
    public oauthSign(@Param({
                         title: 'oauthConsumerKey',
                         tip: 'Twitter app key (consumer key)',
                         type: Type.String
                     }) oauthConsumerKey:AString,
                     @Param({
                         title: 'oauthConsumerSecret',
                         tip: 'Twitter app secret (consumer secret)',
                         type: Type.String
                     }) oauthConsumerSecret:AString,
                     @Param({
                         title: 'oauthToken',
                         tip: 'Twitter user key (user token)',
                         type: Type.String
                     }) oauthToken:AString,
                     @Param({
                         title: 'oauthTokenSecret',
                         tip: 'Twitter user secret (token)',
                         type: Type.String
                     }) oauthTokenSecret:AString,
                     @Param({
                         title: 'httpMethod',
                         tip: 'Http method',
                         type: Type.String
                     }) httpMethod:AString,
                     @Param({
                         title: 'url',
                         tip: 'Request url',
                         type: Type.String
                     }) url:AString,
                     @Param({
                         title: 'queryParams',
                         tip: 'Query params',
                         type: Type.Frame
                     }) queryParams:IFrame,
                     @Param({
                         title: 'body',
                         tip: 'Request body',
                         type: Type.String,
                         optional: true
                     }) body:AString):IFrame
    {
        const timestamp = Math.round(Date.now() / 1000);
        let nonce = EncodingUtil.toBase64(oauthConsumerKey + ':' + timestamp);

        const parameterString = this._createOAuthParameterString(queryParams, oauthConsumerKey.value, oauthToken.value, body, timestamp, nonce);
        const baseString = this._createOAuthBaseString(httpMethod.value, url.value, parameterString);

        const signingKey = this._createOauthSigningKey(oauthConsumerSecret.value, oauthTokenSecret.value);

        const signature = this._createOauthSignature(baseString, signingKey);

        const oauthHeader = `OAuth oauth_consumer_key="${oauthConsumerKey}", oauth_nonce="${nonce}", oauth_signature="${signature}", oauth_signature_method="HMAC-SHA1", oauth_timestamp="${timestamp}", oauth_token="${oauthToken}", oauth_version="1.0"`;

        return Frames.fromStrings(['header', 'value'], [['Authorization', oauthHeader]]);
    }

    @Function({
        title: 'LOGIN_WITH_3RD_PARTY(appOAuth2CredentialsName, authProviderName[, isClientCredentialsFlow])',
        tip: 'Performs authentication with OAuth 2.0 flow.',
        alias: 'LOGIN_WITH_3RD_PARTY',
        example: 'LOGIN_WITH_3RD_PARTY("foo", "google" [,false])',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Frame with token',
                tip: 'Returns google auth token.'
            }
    })
    @bind
    public async loginWith3rdParty(@Param({
                                       title: 'appOAuth2CredentialsName',
                                       tip: 'Application credentials settings name.',
                                       type: Type.String
                                   }) appOAuth2CredentialsName:AString,
                                   @Param({
                                       title: 'authProviderName',
                                       tip: 'Authentication provider name.',
                                       type: Type.String
                                   }) authProviderName:AString,
                                   @Param({
                                       title: 'isClientCredentialsFlow',
                                       tip: 'Set to true if Client Credentials Grant Flow is preferred.',
                                       type: Type.Boolean,
                                       optional: true
                                   }) isClientCredentialsFlow:ABoolean):Promise<IFrame | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();
        const localStorageTokenKey = `${organizationSid}_${authProviderName}_${appOAuth2CredentialsName}`;

        LocalStorageUtil.removeItem(localStorageTokenKey);

        if( isClientCredentialsFlow && isClientCredentialsFlow.value )
        {
            return await this._clientCredentialsLogin(authProviderName, appOAuth2CredentialsName, localStorageTokenKey);
        }
        else
        {
            return await this._authorizationCodeLogin(authProviderName, appOAuth2CredentialsName, organizationSid, localStorageTokenKey);
        }
    }

    private async _authorizationCodeLogin(authProviderName:AString, appOAuth2CredentialsName:AString, organizationSid, localStorageTokenKey:string)
    {
        const credentials:AppOAuth2CredentialsResponse = await commonControllers.appOAuth2.getOAuth2Credentials(
            authProviderName.value as OAuth2ProviderName,
            appOAuth2CredentialsName.value
        );

        let authProviderUrl = await commonControllers.appOAuth2.getAuthProviderConsentScreenUrl(
            authProviderName.value as OAuth2ProviderName,
            credentials
        );

        if( !authProviderUrl )
            return new AError(
                'Wrong authProviderName',
                `Auth provider ${authProviderName} is not supported`
            );

        if( !credentials || !credentials.clientId )
            return new AError(
                'Credentials is not properly configured for this application or provider',
                `Application: ${appOAuth2CredentialsName}, Provider: ${authProviderName}`
            );

        const mintRedirectUrl = `${WindowLocationUtil.getBaseURLForSSE()}/api/app-oauth2/callback`;
        const targetOrigin = WindowLocationUtil.getOrigin();

        const state = JSON.stringify({
            organizationSid: organizationSid,
            applicationName: appOAuth2CredentialsName.value,
            authProviderName: authProviderName.value,
            redirectUrl: mintRedirectUrl,
            targetOrigin: targetOrigin
        });

        const getCodeUrl = `${authProviderUrl}?client_id=${credentials.clientId}&response_type=code&redirect_uri=${mintRedirectUrl}&access_type=offline&scope=${credentials.scopes}&state=${encodeURIComponent(state)}`;
        const tokenObject = await this._openLoginWindow(getCodeUrl, 'AuthWindow', localStorageTokenKey);

        LocalStorageUtil.setItem(localStorageTokenKey, tokenObject);

        return tokenObject
            ? this._convertTokenObjectJsonToFrame(tokenObject, authProviderName.value)
            : this._returnEmptyToken(authProviderName.value);
    }

    private _returnEmptyToken(authProvider:string)
    {
        return JsonUtil.convertJsonObjectToFrame({
            isLoggedIn: false,
            oauthProvider: authProvider
        });
    }

    private _convertTokenObjectJsonToFrame(tokenObjectJson, oauthProvider):IFrame
    {
        const tokenObject = JSON.parse(tokenObjectJson);
        tokenObject.isLoggedIn = true;
        tokenObject.oauthProvider = oauthProvider;

        return JsonUtil.convertJsonObjectToFrame(tokenObject);
    }

    private async _clientCredentialsLogin(authProviderName:AString, appOAuth2CredentialsName:AString, localStorageTokenKey:string)
    {
        const tokenObject = await commonControllers.appOAuth2.clientCredentials(
            authProviderName.value as OAuth2ProviderName,
            appOAuth2CredentialsName.value
        );

        if( tokenObject && tokenObject.accessToken )
        {
            LocalStorageUtil.setItem(localStorageTokenKey, JSON.stringify(tokenObject));
        }

        return JsonUtil.convertJsonObjectToFrame(tokenObject);
    }

    @Function({
        title: 'LOGOUT_3RD_PARTY(appOAuth2CredentialsName, authProviderName)',
        tip: 'Performs logout of OAuth2-logged-in application.',
        alias: 'LOGOUT_3RD_PARTY',
        example: 'LOGOUT_3RD_PARTY("foo", "google")',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'Result of operation',
                tip: 'Returns result of logout operation.'
            }
    })
    @bind
    public async logOutOf3rdParty(@Param({
                                      title: 'appOAuth2CredentialsName',
                                      tip: 'Application credentials settings name.',
                                      type: Type.String
                                  }) appOAuth2CredentialsName:AString,
                                  @Param({
                                      title: 'authProviderName',
                                      tip: 'Authentication provider name.',
                                      type: Type.String
                                  }) authProviderName:AString):Promise<ABoolean | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();
        const localStorageTokenKey = `${organizationSid}_${authProviderName}_${appOAuth2CredentialsName}`;
        const tokenObject = JSON.parse(LocalStorageUtil.getItem(localStorageTokenKey));

        if( tokenObject )
        {
            await commonControllers.appOAuth2.revokeToken(
                appOAuth2CredentialsName.value,
                authProviderName.value as OAuth2ProviderName,
                organizationSid,
                tokenObject.accessToken,
                tokenObject.refreshToken
            );
            LocalStorageUtil.removeItem(localStorageTokenKey);
        }

        return ABoolean.true();
    }

    @Function({
        title: 'IS_LOGGED_IN_3RD_PARTY(appOAuth2CredentialsName, authProviderName)',
        tip: 'Performs check whether user is logged in with 3rd party, refreshes token if any',
        alias: 'IS_LOGGED_IN_3RD_PARTY',
        example: 'IS_LOGGED_IN_3RD_PARTY("foo", "google")',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'Is logged in with 3rd party.',
                tip: 'Returns result whether user is logged in with 3rd party check operation.'
            }
    })
    @bind
    public async isLoggedInWith3rdParty(@Param({
                                            title: 'appOAuth2CredentialsName',
                                            tip: 'Application credentials settings name.',
                                            type: Type.String
                                        }) appOAuth2CredentialsName:AString,
                                        @Param({
                                            title: 'authProviderName',
                                            tip: 'Authentication provider name.',
                                            type: Type.String
                                        }) authProviderName:AString):Promise<ABoolean | AError>
    {
        const loginInfo = await this._get3rdPartyLoginInfo(appOAuth2CredentialsName.value, authProviderName.value as OAuth2ProviderName);
        return new ABoolean(loginInfo.isLoggedIn);
    }

    @Function({
        title: 'GET_3RD_PARTY_LOGIN_INFO(appOAuth2CredentialsName, authProviderName)',
        tip: 'Performs check whether user is logged in with 3rd party, refreshes token if any',
        alias: 'GET_3RD_PARTY_LOGIN_INFO',
        example: 'GET_3RD_PARTY_LOGIN_INFO("foo", "google")',
        icon: '',
        returns:
            {
                type: Type.Boolean,
                title: 'Get 3rd party login information.',
                tip: 'Returns result with 3rd party login information.'
            }
    })
    @bind
    public async get3rdPartyLoginInfo(@Param({
                                          title: 'appOAuth2CredentialsName',
                                          tip: 'Application credentials settings name.',
                                          type: Type.String
                                      }) appOAuth2CredentialsName:AString,
                                      @Param({
                                          title: 'authProviderName',
                                          tip: 'Authentication provider name.',
                                          type: Type.String
                                      }) authProviderName:AString):Promise<IFrame | AError>
    {
        const loginInfo = await this._get3rdPartyLoginInfo(appOAuth2CredentialsName.value, authProviderName.value as OAuth2ProviderName);
        return JsonUtil.convertJsonObjectToFrame(loginInfo);
    }

    private async _get3rdPartyLoginInfo(appOAuth2CredentialsName:string, authProviderName:OAuth2ProviderName):Promise<any>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();
        const localStorageTokenKey = `${organizationSid}_${authProviderName}_${appOAuth2CredentialsName}`;
        const tokenObjectJson = LocalStorageUtil.getItem(localStorageTokenKey);
        if( tokenObjectJson )
        {
            const tokenObject = JSON.parse(tokenObjectJson);
            if( tokenObject.accessToken )
            {
                // in case of error - return null, so we can try and use refreshToken
                const tokenInfoResult = await commonControllers.appOAuth2.getTokenInfo(
                    appOAuth2CredentialsName,
                    authProviderName,
                    tokenObject.accessToken
                ).catch(e => null);

                if( tokenInfoResult && tokenInfoResult['expires_in'] > 0 )
                {
                    return Object.assign(tokenObject, {isLoggedIn: true, oauthProvider: authProviderName});
                }
                else if( tokenObject.refreshToken )
                {
                    const refreshedTokenObject = await commonControllers.appOAuth2.refreshToken(
                        authProviderName,
                        appOAuth2CredentialsName,
                        tokenObject.refreshToken
                    );

                    if( refreshedTokenObject && refreshedTokenObject.accessToken )
                    {
                        const refreshedTokenObjectJson = JSON.stringify(refreshedTokenObject);
                        LocalStorageUtil.setItem(localStorageTokenKey, refreshedTokenObjectJson);
                        return Object.assign(refreshedTokenObject, {isLoggedIn: true, oauthProvider: authProviderName});
                    }
                }
            }
        }

        return {isLoggedIn: false, oauthProvider: authProviderName};
    }

    private _openLoginWindow(url, title, localStorageKey):Promise<string>
    {
        return new Promise<string>(((resolve, reject) =>
        {
            let windowObjectReference = null;
            window.removeEventListener('message', this._receiveMessage);

            const width = 400;
            const height = 500;

            if( this._isMobileDevice() )
            {
                window.location.replace(url);
            }
            else
            {
                windowObjectReference = this._showPopupOnScreenCenter(url, title, width, height);
            }

            const timer = setInterval(function()
            {
                if( windowObjectReference.closed )
                {
                    clearInterval(timer);

                    const resultObjectJson = LocalStorageUtil.getItem(localStorageKey);
                    resolve(resultObjectJson);
                }
            }, 1000);

            window.addEventListener('message', event => this._receiveMessage(event), false);
        }));
    }

    private _isMobileDevice()
    {
        return (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
    };

    private _showPopupOnScreenCenter(url, title, w, h)
    {
        // Fixes dual-screen position                         Most browsers      Firefox
        const dualScreenLeft = window.screenLeft != undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop != undefined ? window.screenTop : window.screenY;

        const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
        const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft;
        const top = (height - h) / 2 / systemZoom + dualScreenTop;
        const popupWindow = window.open(url, title, 'scrollbars=yes, width=' + w / systemZoom + ', height=' + h / systemZoom + ', top=' + top + ', left=' + left);

        // Puts focus on the newWindow
        if( window.focus ) popupWindow.focus();
        return popupWindow;
    }

    private _createOAuthParameterString(queryParams:IFrame, oauthConsumerKey:string, oauthToken:string, body:AString, timestamp:number, nonce:string):string
    {
        let queryParamsObject;
        if( queryParams )
        {
            queryParamsObject = this._coerceFrameToObject(queryParams);
        }

        // Merge oauth params & request params to single object
        let parameterObject = Object.assign({
                oauth_consumer_key: oauthConsumerKey,
                oauth_nonce: nonce,
                oauth_signature_method: 'HMAC-SHA1',
                oauth_timestamp: timestamp,
                oauth_token: oauthToken,
                oauth_version: '1.0'
            },
            queryParamsObject);

        // Sort alphabetically
        let parameterObjectKeys = Object.keys(parameterObject);
        let len = parameterObjectKeys.length;
        parameterObjectKeys.sort();

        // Interpolate to string with format as key1=val1&key2=val2&...
        let parameterString = parameterObjectKeys[0] + '=' + parameterObject[parameterObjectKeys[0]];
        for( var i = 1; i < len; i++ )
        {
            parameterString += '&' + parameterObjectKeys[i] + '=' + EncodingUtil.percentEncode(decodeURIComponent(parameterObject[parameterObjectKeys[i]]));
        }

        parameterString += body ? '&' + body.value : '';

        return parameterString;
    }

    private _createOAuthBaseString(httpMethod:string, url:string, parameterString:string):string
    {
        return `${httpMethod.toUpperCase()}&${EncodingUtil.percentEncode(url)}&${EncodingUtil.percentEncode(parameterString)}`;
    }

    private _createOauthSigningKey(oauthConsumerSecret:string, oauthTokenSecret:string)
    {
        return `${EncodingUtil.percentEncode(oauthConsumerSecret)}&${EncodingUtil.percentEncode(oauthTokenSecret)}`;
    }

    private _createOauthSignature(baseString:string, signingKey:string):string
    {
        const signature = HmacSHA1(baseString, signingKey).toString(enc.Base64);
        return EncodingUtil.percentEncode(signature);
    }

    private _receiveMessage(event:any)
    {
        // Do we trust the sender of this message? (might be
        // different from what we originally opened, for example).
        if( event.origin !== WindowLocationUtil.getBaseURLForSSE() )
        {
            return;
        }

        const {data} = event;

        LocalStorageUtil.setItem(data.key, JSON.stringify(data.token));
    };

    private _coerceFrameToObject(keyValueFrame:IFrame):{}
    {
        const qpPairs = {};
        keyValueFrame.getRecords()
            .forEach(record =>
            {
                const cells = record.getCells();

                const coercedKey = CoercionUtils.coerce(cells[0], Type.String, null);
                const coercedValue = CoercionUtils.coerce(cells[1], Type.String, null);
                if( [coercedKey, coercedValue].some(coerced => Type.isInstanceOf(coerced, Type.Error)) )
                    return new AError('#CANT_COERCE_QUERY_PARAM_TO_STRING');

                const sKey = encodeURIComponent((coercedKey as AString).value);
                const sValue = encodeURIComponent((coercedValue as AString).value);
                qpPairs[sKey] = sValue;
            });

        return qpPairs;
    }
}
